import { useTranslation } from 'react-i18next';
import {
	SaveOutlined,
	SearchOutlined
} from "@ant-design/icons";
import {
	Button,
	Col,
	Form,
	Layout,
	Modal,
	Row,
	TableColumnsType,
	TableProps,
	Tabs,
	TabsProps,
	Typography
} from "antd";
import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import { getListTeacherImportExcelFile } from "../../../service/list-account-teachers/listAccountTeachersApi";
import FormInput from "../../form-input/FormInput";
import TableData from "../../table-data/TableData";
import "./list-teacher-account-import.scss";

type Props = {
	handleClose: () => void
	saveAccount: () => void;
	open: boolean
	importMode: boolean
};

const ListTeacherAccountImportModal = (props: Props) => {
	const { t } = useTranslation();
	const [tab, setTab] = useState("1");
	const { handleClose, open, saveAccount } = props;
	const [searchValue, setSearchValue] = useState<string>("");
	const [listAdminInterface, setListAdminInterface] = useState<any>([]);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(10);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [countReviewed, setCountReviewed] = useState<number>(0);
	const [countAwaitingReview, setCountAwaitingReview] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	const [statusOrder, setStatusOrder] = useState<string>("");
	const [fieldSortOrder, setFieldSortOrder] = useState<string>("")
	const [form] = Form.useForm();

	const getListData = async () => {
		setLoading(true);
		const data = {
			page: pageNumber,
			size: pageSize,
			sort: statusOrder ? [`${fieldSortOrder},${statusOrder}`] : [],
			keyword: searchValue.trim() || undefined,
			status: tab === "1" ? true : false,
		};

		await getListTeacherImportExcelFile(data)
			.then((res) => {
				const { data } = res.data

				setListAdminInterface(data.data)
				setTotalRecords(data.total);
				setCountAwaitingReview(data?.validCount)
				setCountReviewed(data?.invalidCount)

				setTimeout(() => {
					setLoading(false);
				}, 500);
			})
			.catch((err) => {
				setLoading(false);
			});
	};
	useEffect(() => {
		if (open) getListData();
	}, [tab, pageNumber, pageSize, statusOrder, open]);

	useEffect(() => {
		if (open) {
			getListData();
			setTab("1")
		}
	}, [open]);

	const handleSubmitSearch = () => {
		getListData();
	};

	const handleSearch = (e: KeyboardEvent<HTMLSpanElement>) => {
		if (e.key === "Enter") {
			getListData();
		}
	};

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setSearchValue(value);
	};

	const dataColumns: TableColumnsType<any> = [
		{
			title: t("STT", "STT"),
			dataIndex: "numericalOrder",
			key: "numericalOrder",
			render: (_: any, record, index: number) => {
				return <div>{record?.numericalOrder}</div>;
			},
			width: "50px"
		},
		{
			title: t("Email đăng nhập", "Email đăng nhập"),
			dataIndex: "email",
			key: "email",
			sorter: true,
		},
		{
			title: t("Họ và tên", "Họ và tên"),
			dataIndex: "name",
			key: "name",
		},
		{
			title: t("Giới tính", "Giới tính"),
			dataIndex: "gender",
			key: "gender",
			width: "120px"
		},
		{
			title: t("Mã giảng viên", "Mã giảng viên"),
			dataIndex: "code",
			key: "code",
		},
		...tab === "2" ? [
			{
				title: t("Mô tả", "Mô tả"),
				dataIndex: "errorMessage",
				key: "errorMessage",
			},
		] : []
	];

	const handleTableChange: TableProps["onChange"] = (
		pagination,
		filters,
		sorter: any
	) => {
		const data =
			sorter.order === "ascend"
				? "asc"
				: sorter.order === "descend"
					? "desc"
					: "";
		setStatusOrder(data);
		setFieldSortOrder(sorter.field);
	};

	const itemsTab: TabsProps["items"] = [
		{
			key: "1",
			label: (
				<div>
					{t("Tài khoản hợp lệ", "Tài khoản hợp lệ")}
					<span
						className={`number-record ${tab === "1" ? "number-record-active" : "number-record-disabled"
							}`}
					>
						{countAwaitingReview}
					</span>
				</div>
			),
			children: (
				<TableData
					dataColumns={dataColumns}
					loadingTable={loading}
					dataTable={listAdminInterface}
					pageSize={pageSize}
					pageNumber={pageNumber}
					setPageNumber={setPageNumber}
					setPageSize={setPageSize}
					totalRecords={totalRecords}
					scroll={{ x: 1000, y: 500 }}
					pagination={true}
					hideSelection={false}
					handleTableChangeProps={handleTableChange}
				/>
			),
		},
		{
			key: "2",
			label: (
				<div>
					{t("Tài khoản không hợp lệ", "Tài khoản không hợp lệ")}
					<span
						className={`number-record ${tab === "2" ? "number-record-active" : "number-record-disabled"
							}`}
					>
						{countReviewed}
					</span>
				</div>
			),
			children: (
				<TableData
					dataColumns={dataColumns}
					loadingTable={loading}
					dataTable={listAdminInterface}
					pageSize={pageSize}
					pageNumber={pageNumber}
					setPageNumber={setPageNumber}
					setPageSize={setPageSize}
					totalRecords={totalRecords}
					scroll={{ x: 1000, y: 500 }}
					pagination={true}
					hideSelection={false}
					handleTableChangeProps={handleTableChange}
				/>
			),
		},
	];

	const onChange = (key: string) => {
		setTab(key);
		setPageNumber(1);
		form.resetFields();
	};

	const handleCloseModal = () => {
		setTab("1");
		setSearchValue("");
		setListAdminInterface([]);
		setPageNumber(1);
		setPageSize(10);
		setTotalRecords(0);
		setCountReviewed(0);
		setCountAwaitingReview(0);
		setLoading(false);
		setStatusOrder("");
		setFieldSortOrder("");
		form.resetFields();
		handleClose();
	};

	return (
		<Modal
			title={
				<div className="flex algin-center justify-between mr-6">
					<div>{t("Danh sách File tài khoản giảng viên", "Danh sách File tài khoản giảng viên")}</div>
					<div className="flex gap-16">
						<Row className="page-header-group--layout">
							<Col md={{ span: 7 }} lg={{ span: 7 }} xl={{ span: 7 }} xs={{ span: 7 }}>
								<div className="heading-search">
									<FormInput placeholder={t("Nhập từ khóa tìm kiếm...", "Nhập từ khóa tìm kiếm...")} value={searchValue}
										onChange={handleChangeInput}
										onKeyPress={handleSearch}
										style={{ minWidth: "240px" }}
										afterPrefixIcon={<SearchOutlined onClick={handleSubmitSearch} />} />
								</div>
							</Col>
						</Row>
						<Button className="btn btn-primary" onClick={saveAccount}
							disabled={countAwaitingReview === 0}
						>
							<SaveOutlined />
							<Typography.Text>{t("Lưu tài khoản hợp lệ", "Lưu tài khoản hợp lệ")}</Typography.Text>
						</Button>
					</div>
				</div>
			}
			className="list-student-accounts-import"
			open={open}
			onCancel={handleCloseModal}
			width={"90%"}
			centered
			style={{ height: "90vh" }}
			footer={
				<div></div>
			}
		>
			<Layout className="page-header-group course-evaluation">
				<Form.Provider
					onFormFinish={(name, { values, forms }) => {
						if (name === "filterForm") {
							getListData()
						}
					}}
				>
					<div className="tabs-heading pt-3">
						<Tabs items={itemsTab} activeKey={tab} onChange={onChange} />
					</div>
				</Form.Provider>

			</Layout>
		</Modal>
	);
};

export default ListTeacherAccountImportModal;